import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { distinctUntilChanged, map, of } from 'rxjs';

import {
  injectTellentAssistantCurrentUserData,
  injectTellentConversationStartersContext,
} from '@core/tellent-assistant-contracts';
import {
  conversationStartersKey,
  type ConversationStarter,
  type ConversationStartersContext,
} from '@core/tellent-assistant-types';

import { ConversationStartersDataService } from './conversation-starters-data.service';

const GENERAL: ConversationStartersContext = { type: 'recruitment_general' };

@Injectable({ providedIn: 'root' })
export class TellentAssistantConfigService {
  private readonly currentUser = injectTellentAssistantCurrentUserData({ optional: true });
  private readonly startersData = inject(ConversationStartersDataService);
  private readonly context$ =
    injectTellentConversationStartersContext({ optional: true }) ?? of(null);

  public readonly currentContext = toSignal(
    this.context$.pipe(
      map(c => c ?? GENERAL),
      distinctUntilChanged((a, b) => conversationStartersKey(a) === conversationStartersKey(b)),
    ),
    { initialValue: GENERAL },
  );

  public readonly userName: Signal<string> = computed(() => this.currentUser?.userName() ?? '');

  public readonly suggestions: Signal<readonly ConversationStarter[]> = computed(() => {
    const entry = this.startersData.byContext(this.currentContext())();
    return entry?.conversationStarters ?? [];
  });

  public loadStartersFor(ctx: ConversationStartersContext): void {
    this.startersData.loadStarters(ctx).subscribe();
  }
}
